<template>
    <div>
        <DriverTripCard :trip="trip" v-if="trip && !cancelling">
            <template slot="compact">
                <div class="compact-trip-summary">
                    <div
                        class="compact-price-show"
                        v-if="!settings.no_mostrar_precio_hasta_arrancar && settings.modulo_precio_viaje && trip.precio > 0"
                    >
                        <span class="compact-price-label">
                            {{ compactPriceLabel }}
                        </span>
                        <span class="compact-price-value">
                            <span v-lang.precioPrefijo></span>
                            {{ trip.precio | numberFormatter }}
                            <span v-lang.precioSufijo></span>
                        </span>
                    </div>
                    <div class="compact-btn-action-container">
                        <button v-show="canAdvanceReservation && hasNotArrivedYet" class="btn btn-primary btn-heavy compact-trip-btn" v-lang.enDestino @click="onArrivingToTrip">En destino</button>
                        <button v-show="canAdvanceReservation && !hasNotArrivedYet" class="btn btn-primary btn-heavy compact-trip-btn" v-lang.comenzarViaje @click="onStartToTrip">Comenzar viaje</button>
                    </div>
                </div>
            </template>
            <div v-if="!settings.no_mostrar_precio_hasta_arrancar">
                <div class="price-input" v-if="settings.modulo_precio_viaje && currentZona && currentZona.max_recharge > 0 && hasNotArrivedYet">
                    <div class="price-current">
                        <span v-lang.precioActual></span>:
                        <span v-lang.precioPrefijo></span>
                        {{ trip.precio | numberFormatter }}
                        <span v-lang.precioSufijo></span>
                    </div>
                    <label for="trip_price" class="price-label">
                        <span v-lang.ingreseRecargo>Ingrese el recargo</span>
                        <span>
                            ({{ this.translate('maximo') }}:
                            <span v-lang.precioPrefijo></span>
                            {{ currentZona.max_recharge | numberFormatter }}
                            <span v-lang.precioSufijo></span>)
                        </span>
                    </label>
                    <div class="form-input-with-icon--wrapper form-input-with-button--wrapper">
                        <span class="form-label">
                            <svg-item :size="32" :icon="'money'" :color="'--text-color'"></svg-item>
                        </span>
                        <input class="form-input" type="number" v-model.number="recharge" id="trip_price" :disabled="priceUpdated" />
                        <button class="btn btn-transparent" @click="onUpdatePrice" v-if="!priceUpdated">
                            <svg-item :size="32" :icon="'play'" :color="'--main-color'"></svg-item>
                        </button>
                        <span class="btn btn-transparent" v-else>
                            <svg-item :size="32" :icon="'check'" :color="'--main-color'"></svg-item>
                        </span>
                    </div>
                </div>
                <p
                    v-if="showDriverBenefitDiscountHint"
                    class="benefit-discount-hint"
                >{{ translate('beneficioDescuentoPendienteChofer', { pct: beneficioDescuentoPctLabel }) }}</p>

                <div class="price-show" v-if="trip.precio > 0 && currentZona && currentZona.max_recharge <= 0">
                    <span class="price-show-label">
                        {{ !settings.precio_final ? this.translate('precioEstimado') : this.translate('precioFinal') }}
                    </span>
                    <span class="price-show-value">
                        <span v-lang.precioPrefijo></span>
                        {{ trip.precio | numberFormatter }}
                        <span v-lang.precioSufijo></span>
                    </span>
                </div>
            </div>
            <div class="btn-action-container">
                <button v-show="canAdvanceReservation && hasNotArrivedYet" class="btn btn-primary btn-lg btn-heavy " v-lang.enDestino @click="onArrivingToTrip">En destino</button>
                <button v-show="canAdvanceReservation && !hasNotArrivedYet" class="btn btn-primary btn-lg btn-heavy " v-lang.comenzarViaje @click="onStartToTrip">Comenzar viaje</button>
                <button class="btn btn-secondary btn-heavy" v-lang.cancelar @click="openCancelDialog">Cancelar</button>
            </div>
        </DriverTripCard>
        <cancel-card @close_card="onCloseCard" @cancel_trip="onCancelTrip" v-if="cancelling && cancelReasonModuleEnabled"></cancel-card>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DriverTripCard from './DriverTripCard.vue';
import dialogs from '../../../services/dialogs.js';
import { reservationAdvanceAllowed } from '@/services/reservation';

export default {
    name: 'driverOnTrip',
    data () {
        return {
            cancelling: false,
            recharge: 0,
            priceUpdated: false
        };
    },
    computed: {
        ...mapGetters({
            logged: 'auth/checkLogin',
            user: 'auth/user',
            trip: 'driver/actualTrip',
            currentPosition: 'geolocation/actual',
            cancelReasonModuleEnabled: 'company/cancelReasonModuleEnabled',
            settings: 'company/config',
            currentZona: 'geolocation/actualZona'
        }),
        hasNotArrivedYet () {
            return this.trip.frontState !== this.TRIP_STATE.ARRIVED_TO_ORIGIN;
        },
        canAdvanceReservation () {
            return reservationAdvanceAllowed(this.trip, this.settings);
        },
        compactPriceLabel () {
            if (this.currentZona && this.currentZona.max_recharge > 0 && this.hasNotArrivedYet) {
                return this.translate('precioActual');
            }
            return !this.settings.precio_final ? this.translate('precioEstimado') : this.translate('precioFinal');
        },
        pendingBenefitValidation () {
            const pasajero = this.trip && this.trip.pasajero ? this.trip.pasajero : null;
            if (!pasajero || !pasajero.tipo_beneficio) return false;
            const approved = pasajero.beneficio_descuento_aprobado === true ||
                pasajero.beneficio_descuento_aprobado === 1 ||
                pasajero.beneficio_descuento_aprobado === '1';
            const rejected = pasajero.beneficio_descuento_rechazado === true ||
                pasajero.beneficio_estado === 'rechazado' ||
                pasajero.estado_beneficio_descuento === 'rechazado';
            return !approved && !rejected;
        },
        showDriverBenefitDiscountHint () {
            return !!this.trip &&
                this.settings.modulo_descuento_residente_jubilado &&
                this.pendingBenefitValidation &&
                this.beneficioDescuentoPctLabel !== '';
        },
        beneficioDescuentoPctLabel () {
            if (this.settings && this.settings.descuento_residente_jubilado != null) {
                return this.settings.descuento_residente_jubilado;
            }
            return '';
        }
    },
    mounted () {
        this.language = this.$getLang();
    },
    updated () {
    },
    methods: {
        ...mapActions({
            cancelTrip: 'driver/cancelTrip',
            arrivingToTrip: 'driver/arrivingToTrip',
            startTrip: 'driver/startTrip',
            updateTripPrice: 'driver/updateTripPrice'
        }),
        openCancelDialog () {
            console.log('modulo', this.settings.modulo_confirmar_cancelar);
            if (this.settings.modulo_confirmar_cancelar) {
                dialogs.confirm(this.translate('confirmarCancelacion'), (index) => {
                    if (index === 1) {
                        if (this.cancelReasonModuleEnabled) {
                            this.cancelling = true;
                        } else {
                            this.onCancelTrip('RAZON DESACTIVADA: Chofer cancela yendo a buscar');
                        }
                    }
                }, this.translate('cancelarServicio'), [this.translate('si'), this.translate('no')]);
            } else {
                if (this.cancelReasonModuleEnabled) {
                    this.cancelling = true;
                } else {
                    this.onCancelTrip('RAZON DESACTIVADA: Chofer cancela yendo a buscar');
                }
            }
        },
        onCloseCard () {
            this.cancelling = false;
        },
        onCancelTrip (reason) {
            let data = {
                lat: this.currentPosition ? this.currentPosition.lat : 0,
                lng: this.currentPosition ? this.currentPosition.lng : 0,
                comentario: reason
            };
            this.cancelTrip(data);
        },
        onArrivingToTrip () {
            let data = {
                lat: this.currentPosition ? this.currentPosition.lat : 0,
                lng: this.currentPosition ? this.currentPosition.lng : 0
            };
            this.arrivingToTrip(data);
        },
        onStartToTrip () {
            let data = {
                lat: this.currentPosition ? this.currentPosition.lat : 0,
                lng: this.currentPosition ? this.currentPosition.lng : 0
            };
            this.requestBenefitValidationBeforeTrip(data);
        },
        hasPendingBenefitValidation () {
            return this.pendingBenefitValidation;
        },
        getBenefitTypeLabel () {
            if (!this.trip || !this.trip.pasajero || !this.trip.pasajero.tipo_beneficio) return '';
            return this.trip.pasajero.tipo_beneficio === 'residente' ? this.translate('residente') : this.translate('jubilado');
        },
        askConfirmation (message, title = '') {
            return new Promise((resolve) => {
                dialogs.confirm(message, (index) => resolve(index === 1), title, [this.translate('si'), this.translate('no')]);
            });
        },
        async requestBenefitValidationBeforeTrip (data) {
            if (!this.hasPendingBenefitValidation()) {
                this.startTrip(data);
                return;
            }
            const tipoBeneficio = this.getBenefitTypeLabel();
            const showValidationPrompt = await this.askConfirmation(
                this.translate('beneficioValidacionChoferMensaje').replace('{tipo}', tipoBeneficio)
            );
            if (!showValidationPrompt) {
                return;
            }
            const approved = await this.askConfirmation(
                this.translate('beneficioValidacionChoferConfirmacion').replace('{tipo}', tipoBeneficio)
            );
            data.validar_beneficio_descuento = true;
            data.beneficio_descuento_estado = approved ? 'aprobado' : 'rechazado';
            data.beneficio_descuento_aprobado = approved;
            if (this.trip && this.trip.pasajero) {
                this.trip.pasajero.beneficio_descuento_aprobado = approved;
                this.trip.pasajero.beneficio_descuento_rechazado = !approved;
            }
            this.startTrip(data);
        },
        onUpdatePrice () {
            let recharge = parseFloat(this.recharge);
            if (recharge > this.currentZona.max_recharge || recharge < 0) {
                // Invalido
                dialogs.message('El recargo ingresado no puede superar al valor máximo.', { duration: 10, estado: 'error' });
            } else {
                let data = {
                    viaje_id: this.trip.id,
                    recargo: parseFloat(this.recharge)
                };
                console.log(data);
                this.updateTripPrice(data).then(() => {
                    this.priceUpdated = true;
                });
            }
        }
    },
    components: {
        DriverTripCard
    }
};
</script>

<style scoped>
    .price-input {
        margin: 0 calc(3% - 4px);
    }
    .price-current {
        font-size: 1.1em;
        font-weight: bold;
        color: var(--primary-button-bg-color);
    }
    .benefit-discount-hint {
        margin: 0.35rem calc(3% - 4px) 0;
        font-size: 0.9em;
        font-weight: 500;
        line-height: 1.35;
        color: red;
    }
    .compact-trip-summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: .45rem;
    }
    .compact-price-show {
        color: var(--primary-button-bg-color);
        font-size: 1em;
        font-weight: bold;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .compact-price-label {
        margin-right: .2rem;
    }
    .compact-price-value {
        white-space: nowrap;
    }
    .compact-btn-action-container {
        display: flex;
        gap: .35rem;
        flex-shrink: 0;
        margin-left: auto;
    }
    .compact-trip-btn {
        font-size: .95em;
        line-height: 1.1;
        padding: .55em .9em;
        min-height: 0;
    }
</style>



// WEBPACK FOOTER //
// src/components/elements/driver/OnPickingUpTrip.vue